import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { v4 as uuidv4 } from 'uuid';
import {
  getAllRooms,
  saveRoom,
  deleteRoom as deleteRoomFromDB,
  getRoom,
} from '../utils/indexedDb';
import type {
  JsonData,
  StoredRoom,
  UseJsonShareOptions,
  UseJsonShareReturn,
  RoomState,
} from '../types';

// Default WebSocket URL for production
const DEFAULT_WEBSOCKET_URL = 'wss://sync.jsonshare.vliet.io';
// Development WebSocket URL
const DEV_WEBSOCKET_URL = 'ws://localhost:1234';

// Metadata keys for Yjs document
const METADATA_KEY_NAME = 'name';
const METADATA_KEY_IS_ACTIVE = 'isActive';
const METADATA_KEY_INITIATOR_ID = 'initiatorId';

// Helper to extract roomKey from RoomState
export const getRoomKeyFromState = (roomState: RoomState): string | null => {
  if (roomState.type === 'unselected') return null;
  return roomState.roomKey;
};

// Helper to check if state is in a connected state
export const isConnectedState = (roomState: RoomState): boolean => {
  return [
    'sharing',
    'shared',
    'sharingStarting',
    'sharedStarting',
    'sharingStopping',
  ].includes(roomState.type);
};

// Helper to check if user is initiator
export const isInitiatorState = (roomState: RoomState): boolean => {
  return ['sharing', 'sharingStarting', 'sharingStopping'].includes(roomState.type);
};

export const useJsonShare = (
  options: UseJsonShareOptions = {}
): UseJsonShareReturn => {
  const { websocketUrl = DEFAULT_WEBSOCKET_URL, initialJson = {} } = options;
  
  // Use VITE_WEBSOCKET_URL env var if set, otherwise dev/prod defaults
  const effectiveWebsocketUrl = 
    import.meta.env.VITE_WEBSOCKET_URL ||
    (import.meta.env.DEV ? DEV_WEBSOCKET_URL : DEFAULT_WEBSOCKET_URL) ||
    websocketUrl;

  // ========================================================================
  // STATE (Single source of truth: roomState)
  // ========================================================================
  const [json, setJson] = useState<JsonData>(initialJson);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [roomState, setRoomState] = useState<RoomState>({ type: 'unselected' });
  const [rooms, setRooms] = useState<StoredRoom[]>([]);

  // ========================================================================
  // REFS for Yjs and WebSocket
  // ========================================================================
  const yDocRef = useRef<Y.Doc | null>(null);
  const providerRef = useRef<WebsocketProvider | null>(null);
  const yMapRef = useRef<Y.Map<unknown> | null>(null);
  const metadataMapRef = useRef<Y.Map<unknown> | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  
  // Refs to access current state in callbacks (avoid closure issues)
  const roomStateRef = useRef(roomState);
  const roomNameRef = useRef(roomName);
  
  // Keep refs in sync with state
  useEffect(() => {
    roomStateRef.current = roomState;
  }, [roomState]);
  
  useEffect(() => {
    roomNameRef.current = roomName;
  }, [roomName]);

  // ========================================================================
  // HELPER FUNCTIONS (must be declared before useEffect)
  // ========================================================================

  // Helper to clean up all Yjs and WebSocket resources
  const cleanupSharing = useCallback(() => {
    // Call existing cleanup if any
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    
    // Clean up Yjs and WebSocket
    if (providerRef.current) {
      providerRef.current.disconnect();
      providerRef.current.destroy();
      providerRef.current = null;
    }

    if (yDocRef.current) {
      yDocRef.current.destroy();
      yDocRef.current = null;
    }

    yMapRef.current = null;
    metadataMapRef.current = null;
  }, []);

  // ========================================================================
  // DERIVED STATE
  // ========================================================================
  const activeRoomKey = useMemo(() => getRoomKeyFromState(roomState), [roomState]);
  const isConnected = useMemo(() => isConnectedState(roomState), [roomState]);

  // Debug logging for state changes
  useEffect(() => {
    console.log(`[STATE CHANGE ${Date.now()}] New state:`, roomState);
  }, [roomState]);

  // ========================================================================
  // INITIALIZATION
  // ========================================================================
  // Load all rooms from IndexedDB on mount
  useEffect(() => {
    const loadRooms = async () => {
      const storedRooms = await getAllRooms();
      setRooms(storedRooms);
    };
    loadRooms();
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupSharing();
    };
  }, [cleanupSharing]);

  // ========================================================================
  // HELPER FUNCTIONS (continued)
  // ========================================================================

  // Sync JSON to Yjs map
  const syncJsonToYMap = useCallback((newJson: JsonData, yMap?: Y.Map<unknown>) => {
    const map = yMap || yMapRef.current;
    if (map) {
      // Clear existing keys that are not in newJson
      const existingKeys = Array.from(map.keys());
      existingKeys.forEach((key) => {
        if (!(key in newJson)) {
          map.delete(key);
        }
      });
      
      // Set new values
      Object.entries(newJson).forEach(([key, value]) => {
        map.set(key, value);
      });
    }
  }, []);

  // ========================================================================
  // YJS OBSERVERS
  // ========================================================================

  // Yjs change observer
  const observeYMapChanges = useCallback((yMap: Y.Map<unknown>) => {
    const observer = () => {
      // Convert Y.Map to plain JSON object
      const newJson: JsonData = {};
      yMap.forEach((value, key) => {
        newJson[key as string] = value;
      });
      setJson(newJson);
    };
    yMap.observeDeep(observer);
    // Also call once to initialize
    observer();
    return () => {
      yMap.unobserveDeep(observer);
    };
  }, []);

  // Metadata change observer
  const observeMetadataChanges = useCallback((metadataMap: Y.Map<unknown>) => {
    const observer = () => {
      // Get room name from metadata
      const roomNameFromMetadata = metadataMap.get(METADATA_KEY_NAME) as string | undefined;
      const currentRoomName = roomNameRef.current;
      if (roomNameFromMetadata && roomNameFromMetadata !== currentRoomName) {
        console.log(`[SHARED ${Date.now()}] METADATA: Room name updated to:`, roomNameFromMetadata);
        setRoomName(roomNameFromMetadata);
      }
      
      // Get isActive from metadata
      const isActive = metadataMap.get(METADATA_KEY_IS_ACTIVE) as boolean | undefined;
      const currentState = roomStateRef.current;
      console.log(`[SHARED ${Date.now()}] METADATA: current state=${currentState.type}, isActive=${isActive}`);
      
      // If we're in a shared/sharing state and isActive becomes false, transition to stopped state
      // Handle both 'shared' and 'sharedStarting' to catch early metadata changes
      if (currentState.type === 'shared' || currentState.type === 'sharedStarting') {
        if (isActive === false) {
          console.log(`[SHARED ${Date.now()}] METADATA: isActive is FALSE - transitioning to sharedStopped`);
          // Clean up any existing connection
          cleanupSharing();
          setRoomState({ type: 'sharedStopped', roomKey: currentState.roomKey });
          setJson({});
        } else if (isActive === true) {
          console.log(`[SHARED ${Date.now()}] METADATA: isActive is TRUE - all good`);
        } else {
          console.log(`[SHARED ${Date.now()}] METADATA: isActive is UNDEFINED - still waiting for sync`);
        }
      }
    };
    metadataMap.observeDeep(observer);
    // Don't call observer immediately - wait for actual metadata changes
    // This prevents race conditions where metadata isn't synced yet
    console.log(`[SHARED ${Date.now()}] METADATA: Setting up observer (waiting for changes)`);
    return () => {
      metadataMap.unobserveDeep(observer);
    };
  }, [cleanupSharing]);

  // WebSocket connection status observer
  const observeConnectionStatus = useCallback((provider: WebsocketProvider) => {
    const handleStatus = (event: { status: string }) => {
      const currentState = roomStateRef.current;
      const currentlyConnected = isConnectedState(currentState);
      const newConnectedState = event.status === 'connected';
      console.log(`[CONNECTION ${Date.now()}] Status changed: ${event.status}, current state=${currentState.type}, wasConnected=${currentlyConnected}, nowConnected=${newConnectedState}`);
      
      // Only update state if connection status changed
      if (currentlyConnected !== newConnectedState) {
        // Determine new state based on current state and connection
        if (currentState.type === 'sharingStarting' && newConnectedState) {
          console.log(`[INITIATOR ${Date.now()}] Connection established - transitioning to sharing`);
          setRoomState({ type: 'sharing', roomKey: currentState.roomKey });
        } else if (currentState.type === 'sharedStarting' && newConnectedState) {
          console.log(`[SHARED ${Date.now()}] Connection established - transitioning to shared`);
          setRoomState({ type: 'shared', roomKey: currentState.roomKey });
        }
      }
    };
    provider.on('status', handleStatus);
    
    return () => {
      provider.off('status', handleStatus);
    };
  }, []);

  // ========================================================================
  // STATE TRANSITION ACTIONS
  // ========================================================================

  // Create a new local room (does NOT start sharing)
  const createRoom = useCallback(async (name?: string) => {
    console.log(`[INITIATOR ${Date.now()}] createRoom START`);
    // Clean up any existing sharing
    cleanupSharing();

    // Generate new room key and name
    const newRoomKey = uuidv4();
    const newRoomName = name || `Kamer ${Date.now().toString(36).substring(0, 6)}`;

    // Save room to IndexedDB
    await saveRoom({
      key: newRoomKey,
      name: newRoomName,
      json: {}, // Start with empty JSON
      updatedAt: Date.now(),
    });

    console.log(`[INITIATOR ${Date.now()}] createRoom: transitioning to selected state`);
    // Transition to selected state
    setRoomState({ type: 'selected', roomKey: newRoomKey });
    setRoomName(newRoomName);
    setJson({});

    // Load updated rooms list
    const updatedRooms = await getAllRooms();
    setRooms(updatedRooms);
  }, [cleanupSharing]);

  // Select an existing room (load from IndexedDB only)
  const selectRoom = useCallback(async (key: string) => {
    // Clean up any existing sharing
    cleanupSharing();

    // Try to load from IndexedDB
    const room = await getRoom(key);
    if (room) {
      // Set as active room in selected state (not sharing)
      setRoomState({ type: 'selected', roomKey: room.key });
      setRoomName(room.name);
      setJson(room.json);
    }
  }, [cleanupSharing]);

  // Start sharing the currently selected room
  const startSharing = useCallback(async (name?: string) => {
    const currentRoomKey = getRoomKeyFromState(roomState);
    
    // Must have an active room to share
    if (!currentRoomKey) {
      console.warn('No active room to start sharing');
      return;
    }

    // Clean up any existing connection
    cleanupSharing();

    // Get the existing room from IndexedDB
    const existingRoom = await getRoom(currentRoomKey);
    if (!existingRoom) {
      console.warn('Cannot start sharing: room not found in IndexedDB');
      return;
    }

    // Use provided name or existing room name
    const roomNameToUse = name || roomName || existingRoom.name;

    console.log(`[INITIATOR ${Date.now()}] startSharing: transitioning to sharingStarting`);
    // Transition to sharingStarting state
    setRoomState({ type: 'sharingStarting', roomKey: currentRoomKey });
    setRoomName(roomNameToUse);

    // Create new Yjs document
    console.log(`[INITIATOR ${Date.now()}] startSharing: Creating Yjs doc and metadata`);
    const newYDoc = new Y.Doc();
    const newYMap = newYDoc.getMap<unknown>('json');
    const metadataMap = newYDoc.getMap<unknown>('metadata');
    
    // Store room name in metadata
    console.log(`[INITIATOR ${Date.now()}] startSharing: Setting metadata - name=${roomNameToUse}, isActive=true`);
    metadataMap.set(METADATA_KEY_NAME, roomNameToUse);
    // Mark room as active
    metadataMap.set(METADATA_KEY_IS_ACTIVE, true);
    // Store initiator ID
    metadataMap.set(METADATA_KEY_INITIATOR_ID, uuidv4());
    
    // Initialize with current json (set each key-value pair)
    Object.entries(json).forEach(([key, value]) => {
      newYMap.set(key, value);
    });
    
    // Create WebSocket provider with the existing room key
    const newProvider = new WebsocketProvider(
      effectiveWebsocketUrl,
      currentRoomKey,
      newYDoc,
      {
        connect: true,
      }
    );

    // Set up refs
    yDocRef.current = newYDoc;
    providerRef.current = newProvider;
    yMapRef.current = newYMap;
    metadataMapRef.current = metadataMap;

    // Observe changes
    const unobserveYMap = observeYMapChanges(newYMap);
    const unobserveMetadata = observeMetadataChanges(metadataMap);
    const unobserveConnection = observeConnectionStatus(newProvider);

    // Sync initial JSON to Yjs map
    syncJsonToYMap(json, newYMap);

    // Wait for connection and sync
    await new Promise<void>((resolve) => {
      const checkSynced = () => {
        if (newProvider.wsconnected && newProvider.synced) {
          cleanupCheck();
          resolve();
        }
      };
      
      const cleanupCheck = () => {
        newYDoc.off('sync', checkSynced);
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
      
      // Listen for sync events
      newYDoc.on('sync', checkSynced);
      
      // Also check periodically in case sync event doesn't fire
      const intervalId = setInterval(checkSynced, 100);
      
      // Fallback timeout (15 seconds)
      const timeoutId = setTimeout(() => {
        cleanupCheck();
        resolve();
      }, 15000);

      // Store cleanup
      cleanupRef.current = () => {
        cleanupCheck();
      };
    });

    // After sync, ensure metadata is correct (in case server had old values)
    console.log(`[INITIATOR ${Date.now()}] startSharing: Checking metadata after sync`);
    const currentIsActive = metadataMap.get(METADATA_KEY_IS_ACTIVE) as boolean | undefined;
    if (currentIsActive !== true) {
      console.log(`[INITIATOR ${Date.now()}] startSharing: Forcing isActive=true (was ${currentIsActive})`);
      metadataMap.set(METADATA_KEY_IS_ACTIVE, true);
      // Force a transaction to ensure this change is synced
      newYDoc.transact(() => {}, 'force isActive true');
      // Wait a bit for the change to propagate
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.log(`[INITIATOR ${Date.now()}] startSharing: Sync complete, transitioning to sharing`);
    // Transition to sharing state (connection is established)
    setRoomState({ type: 'sharing', roomKey: currentRoomKey });

    // Store cleanup function
    cleanupRef.current = () => {
      unobserveMetadata();
      unobserveYMap();
      unobserveConnection();
      
      if (newProvider) {
        newProvider.disconnect();
        newProvider.destroy();
      }
      newYDoc.destroy();
      yDocRef.current = null;
      providerRef.current = null;
      yMapRef.current = null;
      metadataMapRef.current = null;
      cleanupRef.current = null;
    };
  }, [roomState, json, roomName, effectiveWebsocketUrl, cleanupSharing, observeYMapChanges, observeMetadataChanges, observeConnectionStatus, syncJsonToYMap]);

  // Stop sharing the current room
  const stopSharing = useCallback(async () => {
    const currentRoomKey = getRoomKeyFromState(roomState);
    
    // Must be in a sharing state (initiator)
    if (!['sharing', 'sharingStarting', 'sharingStopping'].includes(roomState.type)) {
      console.warn('Cannot stop sharing: not in a sharing state');
      return;
    }

    const doc = yDocRef.current;
    const provider = providerRef.current;
    const metadataMap = metadataMapRef.current;

    if (!doc || !provider || !metadataMap) {
      // Fallback cleanup if refs are not available
      cleanupSharing();
      setRoomState({ type: 'selected', roomKey: currentRoomKey || uuidv4() });
      return;
    }

    console.log(`[INITIATOR ${Date.now()}] stopSharing: transitioning to sharingStopping`);
    // Transition to sharingStopping state
    setRoomState({ type: 'sharingStopping', roomKey: currentRoomKey! });

    // 1. Set metadata to false
    console.log(`[INITIATOR ${Date.now()}] stopSharing: Setting isActive=false in metadata`);
    metadataMap.set(METADATA_KEY_IS_ACTIVE, false);
    
    // 2. Force a Yjs transaction to ensure the change is queued for sync
    doc.transact(() => {}, 'stop sharing');
    
    console.log(`[INITIATOR ${Date.now()}] stopSharing: Waiting for sync confirmation`);
    // 3. Wait for sync confirmation - this ensures all peers receive the update
    await new Promise<void>((resolve) => {
      const syncHandler = () => {
        doc.off('sync', syncHandler);
        resolve();
      };
      doc.on('sync', syncHandler);

      // Fallback timeout (5 seconds) in case sync doesn't fire
      const timeoutId = setTimeout(() => {
        doc.off('sync', syncHandler);
        resolve();
      }, 5000);

      // Store cleanup for the timeout
      cleanupRef.current = () => {
        clearTimeout(timeoutId);
        doc.off('sync', syncHandler);
      };
    });

    console.log(`[INITIATOR ${Date.now()}] stopSharing: Sync confirmed, saving data to IndexedDB`);
    
    // Save current JSON to IndexedDB before cleanup
    // Extract current JSON from Yjs map
    const yMap = yMapRef.current;
    const currentJson: JsonData = {};
    if (yMap) {
      yMap.forEach((value, key) => {
        currentJson[key as string] = value;
      });
    }
    
    // Save to IndexedDB (reuse existing metadataMap variable from line 460)
    let effectiveRoomName = roomNameRef.current;
    if (!effectiveRoomName && metadataMap) {
      const nameFromMetadata = metadataMap.get(METADATA_KEY_NAME) as string | undefined;
      if (nameFromMetadata) {
        effectiveRoomName = nameFromMetadata;
      }
    }
    effectiveRoomName = effectiveRoomName || `Kamer ${Date.now().toString(36).substring(0, 6)}`;
    
    if (currentRoomKey) {
      await saveRoom({
        key: currentRoomKey,
        name: effectiveRoomName,
        json: currentJson,
        updatedAt: Date.now(),
      });
      const updatedRooms = await getAllRooms();
      setRooms(updatedRooms);
    }

    console.log(`[INITIATOR ${Date.now()}] stopSharing: Data saved, cleaning up`);
    // 4. Now cleanup - metadata is guaranteed to be synced to all peers
    cleanupSharing();
    cleanupRef.current = null;

    console.log(`[INITIATOR ${Date.now()}] stopSharing: transitioning to unselected state`);
    // Transition to unselected state - user needs to select the room again
    setRoomState({ type: 'unselected' });
    setJson({});
  }, [roomState, cleanupSharing, saveRoom, getAllRooms, setRooms, setJson, setRoomState, yMapRef, metadataMapRef, roomNameRef]);

  // Join a room (either from IndexedDB or by connecting to Yjs)
  const joinRoom = useCallback(async (key: string, name?: string) => {
    console.log(`[SHARED ${Date.now()}] joinRoom START - key=${key.substring(0,8)}...`);
    
    // Stop any current sharing
    cleanupSharing();
    cleanupRef.current = null;

    // Try to load from IndexedDB first
    const room = await getRoom(key);
    if (room) {
      console.log(`[SHARED ${Date.now()}] joinRoom: Found in IndexedDB, going to selected state`);
      // Local room - select it (not sharing)
      setRoomState({ type: 'selected', roomKey: room.key });
      setJson(room.json);
      setRoomName(room.name);
      return;
    }

    console.log(`[SHARED ${Date.now()}] joinRoom: NOT in IndexedDB, connecting to Yjs - going to sharedStarting`);
    // Room not in IndexedDB - this is a shared room from someone else
    // Transition to sharedStarting state
    setRoomState({ type: 'sharedStarting', roomKey: key });
    if (name) {
      setRoomName(name);
    }

    const newYDoc = new Y.Doc();
    const newYMap = newYDoc.getMap<unknown>('json');
    const metadataMap = newYDoc.getMap<unknown>('metadata');
    
    // Create WebSocket provider with the existing room key
    console.log(`[SHARED ${Date.now()}] joinRoom: Creating WebSocket provider for room ${key.substring(0,8)}...`);
    const newProvider = new WebsocketProvider(
      effectiveWebsocketUrl,
      key,
      newYDoc,
      {
        connect: true,
      }
    );

    // Set up refs
    yDocRef.current = newYDoc;
    providerRef.current = newProvider;
    yMapRef.current = newYMap;
    metadataMapRef.current = metadataMap;

    // Observe changes
    const unobserveYMap = observeYMapChanges(newYMap);
    const unobserveMetadata = observeMetadataChanges(metadataMap);
    const unobserveConnection = observeConnectionStatus(newProvider);

    console.log(`[SHARED ${Date.now()}] joinRoom: Waiting for sync...`);
    // Wait for the document to be fully synced
    await new Promise<void>((resolve) => {
      const checkSynced = () => {
        console.log(`[SHARED ${Date.now()}] joinRoom: Checking sync - wsconnected=${newProvider.wsconnected}, synced=${newProvider.synced}`);
        if (newProvider.wsconnected && newProvider.synced) {
          cleanupCheck();
          resolve();
        }
      };
      
      const cleanupCheck = () => {
        newYDoc.off('sync', checkSynced);
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
      
      // Listen for sync events
      newYDoc.on('sync', checkSynced);
      
      // Also check periodically in case sync event doesn't fire
      const intervalId = setInterval(checkSynced, 100);
      
      // Fallback timeout (15 seconds)
      const timeoutId = setTimeout(() => {
        console.log(`[SHARED ${Date.now()}] joinRoom: Sync timeout after 15s`);
        cleanupCheck();
        resolve();
      }, 15000);

      // Store cleanup
      cleanupRef.current = () => {
        cleanupCheck();
      };
    });

    console.log(`[SHARED ${Date.now()}] joinRoom: Sync complete! Checking metadata...`);
    // NOW check metadata AFTER sync is complete
    const isActive = metadataMap.get(METADATA_KEY_IS_ACTIVE) as boolean | undefined;
    console.log(`[SHARED ${Date.now()}] joinRoom: isActive=${isActive}`);
    
    if (isActive === true) {
      console.log(`[SHARED ${Date.now()}] joinRoom: isActive is TRUE - transitioning to shared`);
      // Room is active - transition to shared state
      setRoomState({ type: 'shared', roomKey: key });
      
      // Get name from metadata if not provided
      if (!name) {
        const nameFromMetadata = metadataMap.get(METADATA_KEY_NAME) as string | undefined;
        if (nameFromMetadata) {
          setRoomName(nameFromMetadata);
        }
      }
    } else {
      // isActive is false or undefined - the initiator might be in the process of setting it to true
      // Wait a bit and check again to handle race condition
      console.log(`[SHARED ${Date.now()}] joinRoom: isActive is not true, waiting 300ms and checking again...`);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const isActiveAfterWait = metadataMap.get(METADATA_KEY_IS_ACTIVE) as boolean | undefined;
      console.log(`[SHARED ${Date.now()}] joinRoom: After wait, isActive=${isActiveAfterWait}`);
      
      if (isActiveAfterWait === true) {
        console.log(`[SHARED ${Date.now()}] joinRoom: isActive is now TRUE - transitioning to shared`);
        setRoomState({ type: 'shared', roomKey: key });
        if (!name) {
          const nameFromMetadata = metadataMap.get(METADATA_KEY_NAME) as string | undefined;
          if (nameFromMetadata) {
            setRoomName(nameFromMetadata);
          }
        }
      } else {
        console.log(`[SHARED ${Date.now()}] joinRoom: isActive is still not true (${isActiveAfterWait}) - transitioning to sharedStopped`);
        // Room is explicitly NOT active or metadata not available
        // Clean up and transition to sharedStopped
        newProvider.disconnect();
        newProvider.destroy();
        newYDoc.destroy();
        yMapRef.current = null;
        metadataMapRef.current = null;
        providerRef.current = null;
        yDocRef.current = null;
        cleanupRef.current = null;
        
        setRoomState({ type: 'sharedStopped', roomKey: key });
        setJson({});
      }
    }

    // Store cleanup function
    cleanupRef.current = () => {
      unobserveMetadata();
      unobserveYMap();
      unobserveConnection();
      
      if (newProvider) {
        newProvider.disconnect();
        newProvider.destroy();
      }
      newYDoc.destroy();
      yDocRef.current = null;
      providerRef.current = null;
      yMapRef.current = null;
      metadataMapRef.current = null;
      cleanupRef.current = null;
    };
  }, [effectiveWebsocketUrl, observeYMapChanges, observeMetadataChanges, observeConnectionStatus, cleanupSharing]);

  // Delete a room from IndexedDB
  const deleteRoom = useCallback(async (key: string) => {
    await deleteRoomFromDB(key);
    const updatedRooms = await getAllRooms();
    setRooms(updatedRooms);

    // If we're currently in this room, reset
    if (activeRoomKey === key) {
      cleanupSharing();
      setJson({});
      setRoomName(null);
      setRoomState({ type: 'unselected' });
    }
  }, [activeRoomKey, cleanupSharing]);

  // Save current JSON to IndexedDB
  const saveToIndexedDB = useCallback(async (jsonToSave?: JsonData) => {
    const effectiveJson = jsonToSave || json;
    const currentRoomKey = getRoomKeyFromState(roomState);
    
    if (currentRoomKey) {
      const room = await getRoom(currentRoomKey);
      
      // Only save if:
      // 1. We're in a sharing state (initiator), OR
      // 2. The room exists in IndexedDB (local room)
      const shouldSave = isInitiatorState(roomState) || room;
      
      if (shouldSave) {
        // Try to get room name from metadata if not in state
        let effectiveRoomName = roomName;
        if (!effectiveRoomName && metadataMapRef.current) {
          const nameFromMetadata = metadataMapRef.current.get(METADATA_KEY_NAME) as string | undefined;
          if (nameFromMetadata) {
            effectiveRoomName = nameFromMetadata;
          }
        }
        
        // Fallback to generated name
        effectiveRoomName = effectiveRoomName || `Kamer ${Date.now().toString(36).substring(0, 6)}`;
        
        // Always use saveRoom with full object to ensure all fields are saved
        const roomToSave: StoredRoom = {
          key: currentRoomKey,
          name: effectiveRoomName,
          json: effectiveJson,
          updatedAt: Date.now(),
        };
        await saveRoom(roomToSave);
        const updatedRooms = await getAllRooms();
        setRooms(updatedRooms);
      }
    }
  }, [roomState, roomName, json]);

  // Update JSON from external source (react-json-view)
  const handleSetJson = useCallback(async (newJson: JsonData) => {
    const currentRoomKey = getRoomKeyFromState(roomState);
    setJson(newJson);
    syncJsonToYMap(newJson);
    
    // Save to IndexedDB if:
    // 1. We're in a sharing state (initiator), OR
    // 2. The room exists in IndexedDB (local room)
    // Shared rooms (not initiator, not in IndexDB) are NOT saved
    if (currentRoomKey) {
      const roomExistsInDB = await getRoom(currentRoomKey);
      if (isInitiatorState(roomState) || roomExistsInDB) {
        // Pass the new JSON directly to avoid race conditions
        await saveToIndexedDB(newJson);
      }
    }
  }, [roomState, syncJsonToYMap, saveToIndexedDB]);

  return {
    json,
    setJson: handleSetJson,
    roomName,
    isConnected,
    roomState,
    rooms,
    createRoom,
    startSharing,
    stopSharing,
    joinRoom,
    selectRoom,
    deleteRoom,
    saveToIndexedDB,
  };
};
